import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtCurrency } from "../../lib/api";
import { useAuth } from "../../lib/auth-context";
import { useI18n } from "../../lib/i18n";
import MobileHeader from "../../components/MobileHeader";
import { Slider } from "../../components/ui/slider";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import TrustedTraderBadge from "../../components/TrustedTraderBadge";
import { toast } from "sonner";
import {
    Users, ShoppingBag, Wallet as WalletIcon, ArrowRight, CheckCircle2, Search,
    Sparkles, ChevronLeft, AlertCircle, Truck, Briefcase,
} from "lucide-react";

const COLOR = "#7000FF";
const MIN = 1000;
const MAX = 100000;
const TENURES = [30, 60, 90, 120, 150, 180];

export default function SplitPayApply() {
    const nav = useNavigate();
    const { user } = useAuth();
    const { t } = useI18n();

    const isBusiness = user?.role === "business";
    const baseRoute = isBusiness ? "/business" : "/consumer";
    const creditRoute = isBusiness ? "/business/loans" : "/consumer/credit";
    const [step, setStep] = useState(1);
    const [amount, setAmount] = useState(20000);
    const [tenure, setTenure] = useState(60);
    const [purpose, setPurpose] = useState("");
    const [loanType, setLoanType] = useState("peer"); // peer | supplier_bulk (business only)
    const [fundDistribution, setFundDistribution] = useState("initiator");
    const [friends, setFriends] = useState([]);
    const [search, setSearch] = useState("");
    const [picked, setPicked] = useState([]);
    const [quote, setQuote] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/splitpay/friends").then((r) => setFriends(r.data || []));
    }, []);

    useEffect(() => {
        if (picked.length === 0) {
            setQuote(null);
            return;
        }
        api.post("/splitpay/quote", { amount, tenure_days: tenure, co_signer_ids: picked, loan_type: loanType })
            .then((r) => setQuote(r.data))
            .catch(() => setQuote(null));
    }, [amount, tenure, picked, loanType]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return friends;
        return friends.filter((f) =>
            f.full_name?.toLowerCase().includes(q) || f.email?.toLowerCase().includes(q)
        );
    }, [friends, search]);

    const toggle = (id) => {
        setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
    };

    const submit = async () => {
        if (picked.length === 0) {
            toast.error(t("splitpay.toast.invite_min"));
            return;
        }
        setBusy(true);
        try {
            const r = await api.post("/splitpay/apply", {
                amount,
                tenure_days: tenure,
                co_signer_ids: picked,
                fund_distribution: loanType === "supplier_bulk" ? "initiator" : fundDistribution,
                purpose: purpose || null,
                loan_type: loanType,
            });
            toast.success(loanType === "supplier_bulk" ? t("splitpay.toast.bulk_sent") : t("splitpay.toast.sent"));
            nav(`${baseRoute}/splitpay/${r.data.split_loan.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Application failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="splitpay-apply-page">
            <MobileHeader
                title={loanType === "supplier_bulk" ? t("splitpay.bulk_title") : t("splitpay.title")}
                subtitle={t("splitpay.step", { n: step })}
                backTo={creditRoute}
                variant="purple"
            />
            <div className="px-5 -mt-6 pb-6 space-y-4">
                {step === 1 && (
                    <Step1
                        t={t}
                        isBusiness={isBusiness}
                        loanType={loanType} setLoanType={setLoanType}
                        amount={amount} setAmount={setAmount}
                        tenure={tenure} setTenure={setTenure}
                        purpose={purpose} setPurpose={setPurpose}
                        onNext={() => setStep(2)}
                    />
                )}
                {step === 2 && (
                    <Step2
                        t={t}
                        loanType={loanType}
                        friends={filtered}
                        search={search} setSearch={setSearch}
                        picked={picked} toggle={toggle}
                        amount={amount}
                        fundDistribution={fundDistribution}
                        setFundDistribution={setFundDistribution}
                        onBack={() => setStep(1)}
                        onNext={() => setStep(3)}
                    />
                )}
                {step === 3 && (
                    <Step3
                        t={t}
                        amount={amount} tenure={tenure} purpose={purpose}
                        fundDistribution={fundDistribution}
                        loanType={loanType}
                        pickedFriends={friends.filter((f) => picked.includes(f.id))}
                        user={user} quote={quote}
                        busy={busy} onBack={() => setStep(2)} onSubmit={submit}
                    />
                )}
            </div>
        </div>
    );
}

// ----------------------------------------------------------------
// Step 1: amount, tenure, purpose
// ----------------------------------------------------------------
function Step1({ t, isBusiness, loanType, setLoanType, amount, setAmount, tenure, setTenure, purpose, setPurpose, onNext }) {
    const isBulk = loanType === "supplier_bulk";
    return (
        <>
            {isBusiness && (
                <div className="bg-white rounded-2xl border border-slate-200 p-3 product-shadow">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Mode</div>
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            onClick={() => setLoanType("peer")}
                            data-testid="splitpay-mode-peer"
                            className={`p-3 rounded-xl border-2 text-left transition tap-feedback ${
                                loanType === "peer" ? "border-[#7000FF] bg-purple-50" : "border-slate-200"
                            }`}
                        >
                            <Briefcase size={16} className={loanType === "peer" ? "text-[#7000FF]" : "text-slate-400"} />
                            <div className="font-semibold text-sm text-[#0A1930] mt-2">{t("splitpay.mode.peer")}</div>
                            <div className="text-[10px] text-slate-500 leading-tight">{t("splitpay.mode.peer_sub")}</div>
                        </button>
                        <button
                            type="button"
                            onClick={() => setLoanType("supplier_bulk")}
                            data-testid="splitpay-mode-supplier"
                            className={`p-3 rounded-xl border-2 text-left transition tap-feedback ${
                                isBulk ? "border-[#7000FF] bg-purple-50" : "border-slate-200"
                            }`}
                        >
                            <Truck size={16} className={isBulk ? "text-[#7000FF]" : "text-slate-400"} />
                            <div className="font-semibold text-sm text-[#0A1930] mt-2">{t("splitpay.mode.supplier")}</div>
                            <div className="text-[10px] text-slate-500 leading-tight">{t("splitpay.mode.supplier_sub")}</div>
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-2xl border border-slate-200 p-6 product-shadow">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    {isBulk ? <Truck size={14} className="text-[#7000FF]" /> : <ShoppingBag size={14} className="text-[#7000FF]" />}
                    {isBulk ? t("splitpay.step1.title_bulk") : t("splitpay.step1.title_consumer")}
                </div>
                <div className="text-center mt-6">
                    <div className="font-display text-4xl font-bold text-[#7000FF]" data-testid="splitpay-amount-display">
                        {fmtCurrency(amount)}
                    </div>
                </div>
                <Slider
                    min={MIN}
                    max={MAX}
                    step={500}
                    value={[amount]}
                    onValueChange={(v) => setAmount(v[0])}
                    className="mt-6"
                    data-testid="splitpay-amount-slider"
                />
                <div className="flex justify-between text-xs text-slate-400 mt-3">
                    <span>{fmtCurrency(MIN)}</span>
                    <span>{fmtCurrency(MAX)}</span>
                </div>

                <div className="mt-7">
                    <div className="text-sm font-semibold text-slate-700 mb-3">{t("splitpay.step1.tenure")}</div>
                    <div className="grid grid-cols-3 gap-2">
                        {TENURES.map((tn) => (
                            <button
                                key={tn}
                                onClick={() => setTenure(tn)}
                                data-testid={`splitpay-tenure-${tn}`}
                                className={`py-3 rounded-xl border-2 text-sm font-semibold tap-feedback transition ${
                                    tenure === tn ? "border-current text-white bg-[#7000FF] border-[#7000FF]" : "border-slate-200 text-slate-600"
                                }`}
                            >
                                {tn}d
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mt-7">
                    <Label className="text-sm font-semibold text-slate-700">
                        {isBulk ? t("splitpay.step1.purpose_bulk") : t("splitpay.step1.purpose_consumer")}
                    </Label>
                    <Input
                        value={purpose}
                        onChange={(e) => setPurpose(e.target.value)}
                        placeholder={isBulk ? "e.g. 200 bags rice, weekly wholesale" : "e.g. group gift, weekend trip"}
                        className="mt-2 h-11 rounded-xl"
                        data-testid="splitpay-purpose-input"
                        maxLength={120}
                    />
                </div>
            </div>

            <Button
                className="w-full h-12 rounded-xl font-semibold text-base bg-[#7000FF] hover:bg-[#5d00d6]"
                onClick={onNext}
                data-testid="splitpay-step1-continue"
            >
                {isBulk ? t("splitpay.step1.cta_bulk") : t("splitpay.step1.cta_consumer")} <ArrowRight size={16} />
            </Button>
        </>
    );
}

// ----------------------------------------------------------------
// Step 2: pick friends + fund distribution
// ----------------------------------------------------------------
function Step2({ t, loanType, friends, search, setSearch, picked, toggle, amount, fundDistribution, setFundDistribution, onBack, onNext }) {
    const isBulk = loanType === "supplier_bulk";
    const members = isBulk ? picked.length : picked.length + 1;
    const share = members > 0 ? amount / members : amount;

    return (
        <>
            <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2 font-display font-semibold text-[#0A1930]">
                            <Users size={16} className="text-[#7000FF]" /> {t("splitpay.cosigners")}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">{t("splitpay.cosigners.help")}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{t("splitpay.per_share")}</div>
                        <div className="font-display text-lg font-bold text-[#7000FF]" data-testid="splitpay-per-share">
                            {fmtCurrency(share)}
                        </div>
                    </div>
                </div>

                <div className="mt-4 relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search friends"
                        className="pl-9 h-10 rounded-xl"
                        data-testid="splitpay-friends-search"
                    />
                </div>

                <div className="mt-3 space-y-2 max-h-[280px] overflow-y-auto">
                    {friends.length === 0 && (
                        <div className="text-center py-8 text-sm text-slate-400">No friends found</div>
                    )}
                    {friends.map((f) => {
                        const isPicked = picked.includes(f.id);
                        return (
                            <button
                                key={f.id}
                                type="button"
                                onClick={() => toggle(f.id)}
                                className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition tap-feedback text-left ${
                                    isPicked ? "border-[#7000FF] bg-purple-50" : "border-slate-100 hover:border-slate-200"
                                }`}
                                data-testid={`splitpay-friend-${f.id}`}
                            >
                                <Avatar className="size-10 flex-shrink-0">
                                    <AvatarImage src={f.avatar_url} />
                                    <AvatarFallback className="bg-purple-100 text-purple-700 text-xs font-bold">
                                        {f.full_name?.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?"}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <div className="font-semibold text-sm text-[#0A1930] truncate">{f.full_name}</div>
                                        {f.is_trusted_trader && <TrustedTraderBadge size="xs" withTooltip={false} />}
                                    </div>
                                    <div className="text-[11px] text-slate-500 truncate">
                                        Score {f.credit_score} · {f.kyc_status === "verified" ? "✓ Verified" : "Unverified"}{f.role === "business" ? " · Business" : ""}
                                    </div>
                                </div>
                                <span
                                    className={`size-5 rounded-full flex items-center justify-center border-2 flex-shrink-0 transition-colors ${
                                        isPicked ? "bg-[#7000FF] border-[#7000FF]" : "border-slate-300"
                                    }`}
                                    aria-hidden
                                >
                                    {isPicked && <CheckCircle2 size={12} className="text-white" strokeWidth={3} />}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="font-display font-semibold text-[#0A1930] mb-1">
                    {isBulk ? t("splitpay.fund.bulk_title") : t("splitpay.fund.title")}
                </div>
                {isBulk ? (
                    <div className="text-xs text-slate-500 mb-3">
                        {t("splitpay.fund.bulk_help")}
                    </div>
                ) : null}
                {!isBulk && (
                    <div className="grid grid-cols-2 gap-2">
                        <DistributionOption
                            active={fundDistribution === "initiator"}
                            onClick={() => setFundDistribution("initiator")}
                            icon={WalletIcon}
                            label={t("splitpay.fund.initiator")}
                            sub={t("splitpay.fund.initiator_sub")}
                            testid="splitpay-fund-initiator"
                        />
                        <DistributionOption
                            active={fundDistribution === "split"}
                            onClick={() => setFundDistribution("split")}
                            icon={Users}
                            label={t("splitpay.fund.split")}
                            sub={t("splitpay.fund.split_sub")}
                            testid="splitpay-fund-split"
                        />
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" className="h-12 rounded-xl" onClick={onBack} data-testid="splitpay-step2-back">
                    <ChevronLeft size={14} /> Back
                </Button>
                <Button
                    className="h-12 rounded-xl bg-[#7000FF] hover:bg-[#5d00d6] font-semibold"
                    disabled={picked.length === 0}
                    onClick={onNext}
                    data-testid="splitpay-step2-continue"
                >
                    Review <ArrowRight size={14} />
                </Button>
            </div>
        </>
    );
}

function DistributionOption({ active, onClick, icon: Icon, label, sub, testid }) {
    return (
        <button
            onClick={onClick}
            className={`p-3 rounded-xl border-2 text-left transition tap-feedback ${
                active ? "border-[#7000FF] bg-purple-50" : "border-slate-200"
            }`}
            data-testid={testid}
        >
            <Icon size={18} className={active ? "text-[#7000FF]" : "text-slate-400"} />
            <div className="font-semibold text-sm text-[#0A1930] mt-2">{label}</div>
            <div className="text-[11px] text-slate-500 leading-tight mt-0.5">{sub}</div>
        </button>
    );
}

// ----------------------------------------------------------------
// Step 3: review + submit
// ----------------------------------------------------------------
function Step3({ t, amount, tenure, purpose, fundDistribution, loanType, pickedFriends, user, quote, busy, onBack, onSubmit }) {
    const isBulk = loanType === "supplier_bulk";
    const members = isBulk ? pickedFriends.length + 1 : pickedFriends.length + 1;
    const borrowerCount = isBulk ? pickedFriends.length : members;
    return (
        <>
            <div className="bg-gradient-to-br from-[#7000FF] to-[#5d00d6] rounded-2xl p-5 text-white gradient-noise" data-testid="splitpay-review-card">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-white/70 font-bold">{isBulk ? t("splitpay.review.bulk_subtitle") : t("splitpay.review.peer_subtitle")}</div>
                        <div className="font-display text-3xl font-bold mt-1">{fmtCurrency(amount)}</div>
                        <div className="text-xs text-white/80 mt-0.5">{tenure} days · {borrowerCount} {isBulk ? "buyer(s)" : "members"}</div>
                    </div>
                    <div className="size-14 rounded-2xl bg-white/15 flex items-center justify-center">
                        {isBulk ? <Truck size={26} /> : <Users size={26} />}
                    </div>
                </div>
                {purpose && (
                    <div className="mt-4 pt-3 border-t border-white/20 text-xs text-white/80">
                        {isBulk ? "Order" : "Purpose"}: <span className="font-semibold text-white">{purpose}</span>
                    </div>
                )}
            </div>

            {quote && (
                <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow">
                    <div className="text-xs uppercase tracking-wider font-bold text-slate-500">{t("splitpay.review.each_pays")}</div>
                    <div className="font-display text-2xl font-bold text-[#0A1930] mt-1">
                        {fmtCurrency(quote.share_total)}
                    </div>
                    <div className="text-xs text-slate-500">due in {tenure} days</div>
                    <div className="border-t border-slate-100 mt-4 pt-3 space-y-2">
                        <Row label="Principal share" value={fmtCurrency(quote.share_amount)} />
                        <Row label="Interest share" value={fmtCurrency(quote.share_interest)} />
                        <Row label="Fees share" value={fmtCurrency(quote.share_fees)} />
                        <div className="border-t border-slate-100 pt-2 mt-2">
                            <Row label="Group total repayment" value={fmtCurrency(quote.total_repayment)} bold />
                        </div>
                    </div>
                    {quote.interest_savings > 0 && (
                        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-[11px] text-emerald-700 flex items-start gap-1.5">
                            <Sparkles size={12} className="mt-0.5 flex-shrink-0" />
                            <span>
                                <span className="font-bold">{quote.tier_label} tier perk:</span> Saved {fmtCurrency(quote.interest_savings)} on interest at your effective rate of {(quote.effective_rate * 100).toFixed(2)}%
                            </span>
                        </div>
                    )}
                </div>
            )}

            <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="font-display font-semibold text-[#0A1930] mb-3">{isBulk ? "Supplier & buyers" : "Members"}</div>
                <div className="space-y-2">
                    <MemberRow user={user} role={isBulk ? "Supplier (you)" : "Initiator"} />
                    {pickedFriends.map((f) => <MemberRow key={f.id} user={f} role={isBulk ? "Buyer · Pending" : "Pending invite"} />)}
                </div>
            </div>

            <div className="bg-purple-50 border border-purple-100 rounded-xl p-3 text-[11px] text-purple-700 flex items-start gap-2">
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                <span>
                    {isBulk ? (
                        <>Funds extend on credit only after <span className="font-bold">all {pickedFriends.length} buyer(s) accept</span>. Repayments are credited to your wallet as each buyer pays.</>
                    ) : (
                        <>Funds disburse only after <span className="font-bold">all {members} members accept</span>. Each friend will see a pending invite on their home screen.</>
                    )}
                </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" className="h-12 rounded-xl" onClick={onBack} data-testid="splitpay-step3-back">
                    <ChevronLeft size={14} /> Back
                </Button>
                <Button
                    className="h-12 rounded-xl bg-[#7000FF] hover:bg-[#5d00d6] font-semibold"
                    onClick={onSubmit}
                    disabled={busy || !quote}
                    data-testid="splitpay-submit-btn"
                >
                    {busy ? "…" : t("splitpay.submit")}
                </Button>
            </div>
        </>
    );
}

function MemberRow({ user, role }) {
    return (
        <div className="flex items-center gap-3 py-1">
            <Avatar className="size-9">
                <AvatarImage src={user?.avatar_url} />
                <AvatarFallback className="bg-purple-100 text-purple-700 text-[10px] font-bold">
                    {user?.full_name?.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?"}
                </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#0A1930] truncate">{user?.full_name}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">{role}</div>
            </div>
        </div>
    );
}

function Row({ label, value, bold }) {
    return (
        <div className="flex justify-between text-sm">
            <span className="text-slate-500">{label}</span>
            <span className={bold ? "font-display text-base font-bold text-[#0A1930]" : "font-semibold text-[#0A1930]"}>
                {value}
            </span>
        </div>
    );
}
